// Cómo se pinta cada celda de Compromisos: rótulos, tonos y formato. Puro: recibe el dato y
// devuelve texto. Los rótulos son los del mockup `portal-cobrador-compromisos.html`, tal cual.
import dayjs from 'dayjs';
import type { StatsBarItem } from '@/shared/patterns/StatsBar';
import { PROMISE_STAT_KEYS } from './types';
import type { PromiseChannel, PromiseStatKey, PromiseStats, PromiseStatus } from './types';

/** Tono de un chip o de un número. `primary` es el de la pastilla «Mañana» del diseño. */
export type PromiseTone = 'neutral' | 'info' | 'success' | 'danger' | 'warning' | 'primary';

const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

/** Los montos viajan en centavos; a pesos se dividen entre 100 antes de formatear. */
export function formatPromiseMoney(cents: number): string {
    return MXN.format(cents / 100);
}

/** `06/jul/26` — el formato de fecha del diseño para un día de calendario suelto. */
export function formatPromiseDay(day: string): string {
    return dayjs(day).format('DD/MMM/YY');
}

/** Rótulo corto del chip de Canal, el que entra en la celda de la tabla. */
export const PROMISE_CHANNEL_CHIP_LABEL: Record<PromiseChannel, string> = {
    store: 'Tienda',
    oxxo: 'OXXO',
    app: 'App',
    'pcf-auto': 'PCF',
};

/** Rótulo del combo de Canal, que sí tiene ancho para el nombre completo. */
export const PROMISE_CHANNEL_FILTER_LABEL: Record<PromiseChannel, string> = {
    store: 'Tienda',
    oxxo: 'OXXO',
    app: 'App Fiado',
    'pcf-auto': 'PCF automático',
};

/** Los cuatro canales del compromiso como los fija el diseño (M4 §4.5). Van al `title` del chip. */
export const PROMISE_CHANNEL_DESCRIPTION: Record<PromiseChannel, string> = {
    store: 'efectivo en tienda',
    app: 'App Fiado (PCF manual)',
    oxxo: 'OXXO o farmacia con referencia',
    'pcf-auto': 'PCF automático esperando saldo',
};

export const PROMISE_CHANNEL_TONE: Record<PromiseChannel, PromiseTone> = {
    store: 'info',
    oxxo: 'warning',
    app: 'neutral',
    'pcf-auto': 'neutral',
};

export const PROMISE_STATUS_LABEL: Record<PromiseStatus, string> = {
    active: 'Activo',
    kept: 'Cumplido',
    broken: 'Incumplido',
};

export const PROMISE_STATUS_TONE: Record<PromiseStatus, PromiseTone> = {
    active: 'info',
    kept: 'success',
    broken: 'danger',
};

export const PROMISE_STAT_LABEL: Record<PromiseStatKey, string> = {
    all: 'Todos',
    today: 'Hoy',
    tomorrow: 'Mañana',
    broken: 'Incumplidos',
    kept: 'Cumplidos',
};

export const PROMISE_STAT_TONE: Record<PromiseStatKey, PromiseTone> = {
    all: 'neutral',
    today: 'info',
    tomorrow: 'primary',
    broken: 'danger',
    kept: 'success',
};

/**
 * Las 5 pastillas de Compromisos para la `<StatsBar>`, en el orden del diseño. Cuentan sobre TODOS
 * los compromisos del cobrador, no sobre lo que dejaron los filtros.
 */
export function promisesStatsItems(stats: PromiseStats): StatsBarItem<PromiseStatKey>[] {
    return PROMISE_STAT_KEYS.map((key) => ({
        key,
        label: PROMISE_STAT_LABEL[key],
        value: stats[key],
        tone: PROMISE_STAT_TONE[key],
    }));
}

/** Color del número de una pastilla y del texto de un chip. */
export const TONE_TEXT_CLASS: Record<PromiseTone, string> = {
    neutral: 'text-foreground',
    info: 'text-info',
    success: 'text-success',
    danger: 'text-danger',
    warning: 'text-warning',
    primary: 'text-primary',
};

/** Relleno + borde del chip, como `.chip-*` del mock: los dos llevan el color del tono. */
export const TONE_CHIP_CLASS: Record<PromiseTone, string> = {
    neutral: 'border-border bg-muted text-muted-foreground',
    info: 'border-info-soft-border bg-info-soft text-info',
    success: 'border-success-soft-border bg-success-soft text-success',
    danger: 'border-danger-soft-border bg-danger-soft text-danger',
    warning: 'border-warning-soft-border bg-warning-soft text-warning',
    primary: 'border-primary bg-primary/10 text-primary',
};

/** La sub-línea del crédito en el detalle: `CR-3004 · 62d mora`, o «al día» cuando no debe nada. */
export function creditArrearsCaption(creditId: string, arrearsDays: number): string {
    return `${creditId} · ${arrearsDays > 0 ? `${String(arrearsDays)}d mora` : 'al día'}`;
}
